"use client";

import { ShowLessButton, ViewMoreButton } from "@punchup/ui-common";
import { UserEvents, trackEvent } from "@punchup/analytics";

import { CollectionRow } from "@/data/v2/models/collections";
import { Comedian } from "@/data/comedians";
import { SpecialCard } from "../cards/SpecialCard";
import { SpecialWithUnlocked } from "@/data/specials";
import { useSearchParams } from "next/navigation";
import { useState } from "react";

const INITIAL_PAGE_SIZE = 2;

export default function BundleSpecialList({
  comedian,
  associatedSpecials,
  userCanAccessBundle,
  bundle,
}: {
  comedian: Comedian;
  associatedSpecials: SpecialWithUnlocked[];
  userCanAccessBundle: boolean;
  bundle: CollectionRow;
}) {
  const [showAll, setShowAll] = useState(false);
  const currentParams = useSearchParams();

  if (!associatedSpecials?.length) return null;

  const handleSeeAllClick = () => {
    trackEvent(UserEvents.CLICKED_SEE_MORE_SPECIALS, {
      ComedianId: comedian.id,
    });
    setShowAll(true);
  };

  const handleShowLessClick = () => {
    setShowAll(false);
  };

  const createAccessibleBundleLink = (special: SpecialWithUnlocked) => {
    const params = new URLSearchParams(currentParams);
    params.set("bundleId", bundle.id);
    params.delete("specialId");
    return `/${comedian.slug}/${special.slug}?${params.toString()}`;
  };

  const createBundlePreviewLink = (special: SpecialWithUnlocked) => {
    const params = new URLSearchParams(currentParams);
    params.set("specialId", special.id);
    return `/${comedian.slug}/bundles/${bundle.slug}?${params.toString()}`;
  };
  const displayedSpecials = showAll
    ? associatedSpecials
    : associatedSpecials.slice(0, INITIAL_PAGE_SIZE);

  const hasMoreContent = associatedSpecials.length > INITIAL_PAGE_SIZE;

  return (
    <div className="pb-12 md:pb-20 px-4 md:px-20 xl:mx-auto max-w-7xl">
      <h4 className="text-xl md:text-3xl mb-4">Videos in this Collection</h4>
      <div className="flex flex-col gap-y-6 md:gap-y-10">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-6">
          {displayedSpecials.map((special: SpecialWithUnlocked) => (
            <SpecialCard
              location="bundlePage"
              key={special.id}
              comedian={comedian}
              special={special}
              width={411}
              height={236}
              mobileWidth={165}
              mobileHeight={165}
              useSquareImgOnMobile
              mobileClassName="flex flex-row justify-items-center gap-x-5 w-full"
              hideLockOverlay={true}
              customLink={
                userCanAccessBundle
                  ? createAccessibleBundleLink(special)
                  : createBundlePreviewLink(special)
              }
            />
          ))}
        </div>

        {hasMoreContent && (
          <div className="flex flex-row justify-center w-full">
            {!showAll ? (
              <ViewMoreButton
                label={`Show All (${associatedSpecials.length})`}
                onClick={handleSeeAllClick}
                className="w-full py-5"
              />
            ) : (
              <ShowLessButton
                label="Show Less"
                onClick={handleShowLessClick}
                className="w-full py-5"
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
